import { useEffect, useState } from 'react';
import { useBreakpoints } from './useBreakpoints';
import { useFeatureFlagProperties } from './queries/one-xp';
import { useUpdateUserAttributes, useUserAttributes } from './queries/users';
import { GetWalkmeDataFunc, WalkmeModel, WalkmeName, WalkmeStep } from 'src/types/walkme.model';
import getWalkmeData, {
    isTodayInTheShowWalkmeTimeWindow,
    isTodayOnOrAfterReleaseDate,
    isWalkmeFeatureFlagTimeDependent
} from 'src/utils/walkme.utils';
import { UserAttributes } from 'src/types/user-attributes.enum';
import { useLocation } from 'react-router';

export default function useWalkme(walkmeName: WalkmeName): {
    isLoading: boolean;
    isEnabled: boolean;
    isNewUser: boolean;
    isOnOrAfterReleaseDate: boolean;
    incrementStepIndex: (index: number) => Promise<void>;
    markAsDone: () => Promise<void>;
    getStepData: (index: number) => WalkmeStep;
} {
    const dataFunction: GetWalkmeDataFunc = getWalkmeData(walkmeName);
    const { pathname } = useLocation();

    // STATE
    const [isEnabled, setIsEnabled] = useState<boolean>(false);
    const [walkmeModelData, setWalkmeModelData] = useState<WalkmeModel>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isNewUser, setIsNewUser] = useState<boolean>(false); // default to false since absence of attribute means not new
    const [isOnOrAfterReleaseDate, setIsOnOrAfterReleaseDate] = useState<boolean>(false);

    // HOOKS
    const breakpoints = useBreakpoints();
    const { data: featureFlagProperties, isLoading: featureFlagLoading } = useFeatureFlagProperties(
        walkmeModelData?.featureFlagName || ''
    );
    const { data: userAttributeValues, isLoading: userAttributesLoading, isError, refetch } = useUserAttributes();
    const { mutateAsync: updateUserAttributes } = useUpdateUserAttributes();

    // EFFECT
    useEffect(() => {
        if (breakpoints) {
            setWalkmeModelData(dataFunction(breakpoints));
        }

        if (featureFlagProperties && walkmeModelData) {
            if (!isWalkmeFeatureFlagTimeDependent(walkmeModelData)) {
                // Not time-dependent, so feature is enabled since featureFlagProperties is not null
                setIsEnabled(true);
            } else {
                // Feature flag is time-dependent, check to see if today is in the time window
                setIsEnabled(isTodayInTheShowWalkmeTimeWindow(walkmeModelData, featureFlagProperties));
            }
            setIsOnOrAfterReleaseDate(isTodayOnOrAfterReleaseDate(walkmeModelData, featureFlagProperties));
        }

        setIsLoading(featureFlagLoading || userAttributesLoading);

        if (isError) {
            console.log('useWalkme error', isError);
        }

        if (!userAttributesLoading && userAttributeValues) {
            if (Object.keys(userAttributeValues).includes(UserAttributes.IS_NEW_USER)) {
                setIsNewUser(userAttributeValues[UserAttributes.IS_NEW_USER]);
            }
        }
    }, [walkmeName, breakpoints, featureFlagProperties, featureFlagLoading, userAttributesLoading, isError]);

    // METHODS
    const incrementStepIndex = async (currentStepIndex: number): Promise<void> => {
        await updateUserAttributes({
            ...userAttributeValues,
            [walkmeModelData?.startupStepUserAttributeName]: currentStepIndex + 1
        });
        await handleRefetch();
        const isLastStep: boolean = currentStepIndex + 1 === walkmeModelData?.steps?.length;
        if (isLastStep) {
            if (walkmeModelData.redirectOnFinish && walkmeModelData.redirectOnFinishUrl)
                window.location.assign(walkmeModelData.redirectOnFinishUrl);
        } else {
            if (walkmeModelData?.steps[currentStepIndex + 1].path !== pathname)
                window.location.assign(walkmeModelData?.steps[currentStepIndex + 1].path);
        }
    };

    const markAsDone = async (): Promise<void> => {
        await updateUserAttributes({
            ...userAttributeValues,
            [walkmeModelData?.startupStepUserAttributeName]: walkmeModelData?.steps?.length // mark as done, while allowing for future additional steps
        });
        await handleRefetch();
    };

    const handleRefetch = async (): Promise<void> => {
        if (refetch) await refetch();
    };

    const getStepData = (index: number): WalkmeStep => {
        return index < (walkmeModelData?.steps?.length || 0) ? walkmeModelData?.steps[index] : null;
    };

    return {
        isLoading,
        isEnabled,
        isNewUser,
        isOnOrAfterReleaseDate,
        incrementStepIndex,
        markAsDone,
        getStepData
    };
}
